import { get, put, post } from '@private-crm-app/requests/api';
import { getCRMToken, getPrivateAppToken } from '@private-crm-app/utils';

export const getLoggedInUser = (email: string) => {
  const queryParams = `q=${encodeURIComponent(email)}&type=${ContactType.INDIVIDUAL}&isSourceAuth0=true`;
  return get<IContactFetchRequest>({ url: `contacts?${queryParams}` });
};

export const getAuth0UserContact = (email: string, auth0UserId: string) => {
  return get<IContactFetchRequest>({
    url: `contacts/auth0-user?email=${encodeURIComponent(email)}&auth0UserId=${encodeURIComponent(auth0UserId)}`,
  });
};

export const getAuth0UserContactByPhone = (phoneNumber: string, auth0UserId: string) => {
  return get<IContactFetchRequest>({
    url: `contacts/auth0-user?phoneNumber=${encodeURIComponent(phoneNumber)}&auth0UserId=${encodeURIComponent(auth0UserId)}`,
  });
};

export const findContactByPhoneNumber = (phoneNumber: string) => {
  return get<IContactFetchRequest>({
    url: `contacts/phone?phoneNumber=${encodeURIComponent(phoneNumber)}`,
  });
};

export const createLinkSubmissionRequest = () =>
  post<{ created: boolean; issueId?: number; CSNumber?: string | null }>({
    url: 'contacts/link-submission-request',
    data: {},
  });

export const findContactsByEmail = (email: string | null, currentContactId: number, phone?: string | null) => {
  const queryParams = new URLSearchParams({
    checkCrmIssues: 'true',
    includeInactive: 'false',
  });

  // Add email or phone based on what's provided
  if (email) {
    queryParams.append('email', email);
  } else if (phone) {
    queryParams.append('phone', phone);
  }

  if (currentContactId) {
    queryParams.append('excludeCurrentContactId', currentContactId.toString());
  }

  return get<IContactFetchRequest>({
    url: `contacts/email?${queryParams.toString()}`,
  });
};

export const mergeContactsByIds = (contactIds: number[], mergeToContactId: number) => {
  const crmToken = getCRMToken();
  const agencyId = localStorage.getItem('agency_id') || '1';

  return post<any>({
    url: 'contacts/merge-contacts',
    data: {
      contactIds,
      mergedContactFields: {},
      mergeToContactId,
      workspaceId: null,
      agencyId: Number(agencyId),
    },
    headers: {
      'crm-token': crmToken,
    },
  });
};

export const reassignSubmissions = (sourceContactId: number, targetContactId: number) => {
  const crmToken = getCRMToken();

  return post<IReassignSubmissionsResponse>({
    url: 'contacts/reassign-submissions',
    data: {
      sourceContactId,
      targetContactId,
    },
    headers: {
      'crm-token': crmToken,
    },
  });
};

export const sendVerificationEmail = (email: string, auth0UserId?: string) => {
  const crmToken = localStorage.getItem('crm_token');

  if (!crmToken) {
    return Promise.reject(new Error('CRM token not found'));
  }

  return post<{ message: string }>({
    url: 'users/verify',
    data: { email, crmToken, auth0UserId },
  });
};

export const editEmailRequest = (newEmail: string) => {
  return post<{ message: string }>({
    url: 'users/editEmail',
    data: { newEmail },
  });
};

export const verifyEmailChange = (token: string) => {
  return get<{
    success: boolean;
    message: string;
    contactId: number;
    oldEmail: string;
    newEmail: string;
  }>({
    url: `users/verifyEmailChange?token=${encodeURIComponent(token)}`,
  });
};

export const verifyEmail = (token: string) => {
  return get<{ success: boolean; message: string; userId: number; email: string }>({
    url: `users/verify?token=${encodeURIComponent(token)}`,
  }).catch((error) => {
    if (error.response?.status === 400 && error.response?.data?.message?.includes('already verified')) {
      return {
        success: true,
        message: 'Email already verified',
        userId: error.response?.data?.userId || 0,
        email: error.response?.data?.email || '',
      };
    }
    throw error;
  });
};

export const updateLoggedInUser = (contactId: number, contact: IContactRequest) =>
  put<IContactResponse>({ url: `contacts/${contactId}`, data: contact });

export const completeOnboarding = (
  contactId: number | string,
  contactInfo: {
    firstName: string;
    lastName: string;
    auth0UserId?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
  }
) => {
  if (!contactId) {
    console.error('Missing contact ID for completeOnboarding:', contactId);
    return Promise.reject(new Error('Contact ID is required'));
  }

  console.log(`Making completeOnboarding request for contact ${contactId}`);
  return put<IContactResponse>({
    url: `users/${contactId}/complete-onboarding`,
    data: contactInfo,
  });
};

export const fetchEditStatus = (contactId: number) => {
  const headers = {
    'crm-token': getCRMToken(),
  };

  return get<{ allowProfileEdit: boolean }>({ url: `users/profile/${contactId}/profile-edit-status`, headers });
};

export const createContact = (payload: any, { skipCustomFields = false } = {}) => {
  return post({
    url: `contacts${skipCustomFields ? '?skipCustomFields=true' : ''}`,
    data: payload,
  });
};

export const getContactCustomFields = () => {
  const token = getPrivateAppToken();
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  return get<IContactCustomFieldResponse[]>({
    url: 'config/contact-custom-fields',
    headers,
  });
};

export const getAuth0UserByPhoneNumber = (phoneNumber: string, auth0UserId?: string) => {
  console.log(
    `API call to get Auth0 primary user by phone number: ${
      phoneNumber ? phoneNumber.substring(0, 6) + '...' : 'undefined'
    }`
  );

  const queryParams = new URLSearchParams({
    phoneNumber,
  });

  if (auth0UserId) {
    queryParams.append('auth0UserId', auth0UserId);
  }

  return get<{
    message: string;
    user: {
      user_id: string;
      email: string;
      email_verified: boolean;
      phone_number: string;
      phone_verified: boolean;
      name: string;
      given_name: string;
      family_name: string;
      picture: string;
      identities: any[];
      created_at: string;
      updated_at: string;
      last_login: string;
      isPrimary: boolean;
    };
    totalUsersFound: number;
  }>({
    url: `contacts/auth0-user/by-phone?${queryParams.toString()}`,
  });
};

export const initiatePhoneChange = (newPhone: string) => {
  return post<{ transactionId: string; expiresAt: string }>({
    url: 'profile/phone-change/initiate',
    data: { newPhone },
  });
};

export const verifyPhoneChange = (transactionId: string, code: string) => {
  return post<{ success: boolean }>({
    url: 'profile/phone-change/verify',
    data: { transactionId, code },
  });
};

export const getAgencyFlags = (agencyId: number) => {
  return get<{
    isAccountsFeatureFlagEnabled: boolean;
    isPublicMapEnabled: boolean;
    isMobileAppEnabled: boolean;
    appleAuth0ConnectionName: string | null;
  }>({
    url: `agency/${agencyId}/flags`,
    headers: { 'x-agency-id': String(agencyId) },
  });
};

export const resetCitizenPassword = (email: string) => {
  return post<unknown>({
    url: 'auth0-forgot-password',
    data: { email },
  });
};

export const dismissTransferAlert = (contactId: number) => {
  return put<{ message: string }>({
    url: `contacts/${contactId}/dismiss-transfer-alert`,
    data: {}
  });
};
